import { Circle, ShieldCheck, ExternalLink, Download } from "lucide-react";
import { COMPANY_DATA, NAICS, SAM_URL, CAP_STATEMENT } from "../../data/content";

const Overline = ({ children, num }) => (
  <div className="flex items-center gap-4 mb-5">
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">{num}</span>
    <span className="h-px w-10 bg-white/20" />
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">{children}</span>
  </div>
);

export const CompanyData = () => {
  return (
    <section id="company-data" data-testid="company-data-section" className="relative border-t border-white/10 bg-[#0C0C0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Overline num="04">Company Credentials</Overline>
        <h2 className="font-sans font-black uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl text-white max-w-3xl leading-[0.95]">
          Registered, Active & <span className="metal-text">Ready To Quote</span>
        </h2>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <a
            href={SAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="sam-verify-btn"
            className="group inline-flex items-center gap-3 border border-white/20 bg-[#121212] px-6 py-3.5 hover:border-white/50 hover:bg-[#1A1A1A] transition-all"
          >
            <ShieldCheck size={20} strokeWidth={1.8} className="text-emerald-400" />
            <span className="font-sans font-bold uppercase tracking-widest text-sm text-white">Verify Us on SAM.gov</span>
            <ExternalLink size={15} className="text-neutral-500 group-hover:text-white transition-colors" />
          </a>
          <a
            href={CAP_STATEMENT}
            download
            data-testid="credentials-download-btn"
            className="group inline-flex items-center gap-3 bg-white text-black px-6 py-3.5 hover:bg-neutral-300 transition-colors"
          >
            <Download size={20} strokeWidth={1.8} />
            <span className="font-sans font-bold uppercase tracking-widest text-sm">Download Capability Statement</span>
          </a>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Terminal data panel */}
          <div className="lg:col-span-7 relative border border-[#4B5563] bg-black scan-line">
            <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
              <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">atlas // entity-record</span>
            </div>
            <div className="p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {COMPANY_DATA.map((d) => (
                <div key={d.label} data-testid={`company-data-${d.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">{d.label}</span>
                  <span className="font-mono text-base md:text-lg text-white break-words">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* NAICS panel */}
          <div className="lg:col-span-5 border border-white/10 bg-[#121212] p-6 md:p-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-6">Target NAICS Codes</p>
            <ul className="space-y-5">
              {NAICS.map((n) => (
                <li key={n.code} data-testid={`naics-${n.code}`} className="flex gap-4">
                  <span className="font-mono text-base font-bold text-white whitespace-nowrap">{n.code}</span>
                  <span className="font-body text-sm text-neutral-400 leading-snug">{n.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Past performance */}
        <div data-testid="past-performance" className="mt-5 border border-white/10 bg-[#121212] p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex items-center gap-3">
            <Circle size={14} className="text-emerald-400 fill-emerald-400 animate-pulse" />
            <span className="font-sans font-bold uppercase tracking-tight text-2xl text-white whitespace-nowrap">Past Performance</span>
          </div>
          <p className="font-body text-neutral-400 text-sm md:text-base leading-relaxed">
            As a newly established Veteran-Owned Small Business, Atlas does not yet have prior contract
            past performance to report. We are actively seeking opportunities and teaming arrangements to
            build our record, and we bring disciplined sourcing, transparent coordination, and a
            partner-forward execution model to every engagement.
          </p>
        </div>
      </div>
    </section>
  );
};
